/**
 * What a bulk action is about to act on.
 *
 * The distinction this type exists to keep is the one the page is built around: **the queue** is not the
 * same thing as **the rows currently on screen**. A page holds at most a hundred rows, and a queue holds
 * however many stacks are pending — a thousand on a large scope. A select-all that ticked every visible
 * id would therefore promise "deploy everything" and quietly deliver the first page of it, which is the
 * failure mode this page was rebuilt to remove.
 *
 * So the two are separate cases rather than one set that select-all happens to fill:
 *
 * - `rows` — an explicit list of stack groups, which is the ten-stack customer ticking three of them.
 * - `queue` — everything the current tab, search and type resolve to, whatever its size and whatever has
 *   been fetched. It travels to the server as an *absent* `only`, so the set is resolved there by the
 *   same aggregation and the same Node gate that produced the rows.
 *
 * Reaching `queue` is deliberately a second, explicit click (see `offersWholeQueue`): ticking the header
 * selects the page, and selecting more than the page is something the user asks for in as many words.
 */
export type DeploymentSelection = { kind: 'rows'; ids: string[] } | { kind: 'queue' }

export const EMPTY_SELECTION: DeploymentSelection = { kind: 'rows', ids: [] }

/**
 * How many stack groups the action would cover.
 *
 * `queue` answers with the tab total rather than with anything counted on the client — that total comes
 * from the same `$facet` as the rows, so the number on the button is the number the server will resolve.
 */
export const selectedCount = (selection: DeploymentSelection, queueTotal: number): number => (selection.kind === 'queue' ? queueTotal : selection.ids.length)

/** In `queue` mode every row is selected, including the ones no page has fetched. */
export const isRowSelected = (selection: DeploymentSelection, stackGroupId: string): boolean => selection.kind === 'queue' || selection.ids.includes(stackGroupId)

/**
 * Tick or untick one row.
 *
 * Unticking while the whole queue is selected drops to the visible page minus that row. It cannot mean
 * "the queue except this one" — nothing in the wire format can express an exclusion — and narrowing to
 * something the user can see and count is the honest reading of the click, where keeping `queue` would
 * ignore it outright.
 */
export const toggleRow = (selection: DeploymentSelection, stackGroupId: string, pageIds: string[]): DeploymentSelection => {
  if (selection.kind === 'queue') {
    return { kind: 'rows', ids: pageIds.filter((id) => id !== stackGroupId) }
  }
  return selection.ids.includes(stackGroupId) ? { kind: 'rows', ids: selection.ids.filter((id) => id !== stackGroupId) } : { kind: 'rows', ids: [...selection.ids, stackGroupId] }
}

/** The header checkbox: selects **the page**, never more. Widening past it is `selectWholeQueue`. */
export const togglePage = (selection: DeploymentSelection, pageIds: string[]): DeploymentSelection => (isPageFullySelected(selection, pageIds) ? EMPTY_SELECTION : { kind: 'rows', ids: pageIds })

export const selectWholeQueue = (): DeploymentSelection => ({ kind: 'queue' })

const isPageFullySelected = (selection: DeploymentSelection, pageIds: string[]): boolean => selection.kind === 'queue' || (pageIds.length > 0 && pageIds.every((id) => selection.ids.includes(id)))

/** Checked when the page is covered, indeterminate on a partial tick — the usual three states. */
export const headerCheckboxState = (selection: DeploymentSelection, pageIds: string[]): { checked: boolean; indeterminate: boolean } => {
  const checked = isPageFullySelected(selection, pageIds)
  return { checked, indeterminate: !checked && pageIds.some((id) => isRowSelected(selection, id)) }
}

/**
 * Whether to offer "select all N in this queue".
 *
 * Only once the page is fully ticked and the queue is bigger than it — before that the offer is noise,
 * and after `queue` is chosen there is nothing left to widen to.
 */
export const offersWholeQueue = (selection: DeploymentSelection, pageIds: string[], queueTotal: number): boolean =>
  selection.kind === 'rows' && isPageFullySelected(selection, pageIds) && queueTotal > pageIds.length

/**
 * What to send as the action's `only`.
 *
 * `undefined` for the whole queue is the contract's own spelling of it (`isWholeQueueAction`), and is
 * what keeps a thousand-stack run one request rather than one per page of ids.
 */
export const selectionOnly = (selection: DeploymentSelection): string[] | undefined => (selection.kind === 'queue' ? undefined : selection.ids)
